import React from "react";
import { Badge } from "../Badge/Badge";
import { Button } from "../Button/Button";
import { Progress } from "../Progress/Progress";
import { ClockIcon } from "Components/Icons/Miscellaneous/ClockIcon";
import { DownloadIcon } from "Components/Icons/Miscellaneous/DownloadIcon";
import { HeartOutlineIcon } from "Components/Icons/Miscellaneous/HeartOutlineIcon";
import { HeartFilledIcon } from "Components/Icons/Miscellaneous/HeartFilledIcon";
import { CircularProgress } from "@mui/material";
import notFound from "Recursos/notFound.png";
import "./CourseCard.scss";

/**
 * Componente de Card de curso con diferentes variantes y estados
 * @param {Object} props - Props del componente
 * @param {string} props.title - Título del curso
 * @param {string} props.imageUrl - URL de la imagen de portada del curso
 * @param {string} [props.duration] - Duración del curso (ej: "2h 30min")
 * @param {string} [props.difficulty] - Dificultad del curso
 * @param {string} [props.language] - Idioma del curso (ej: "Español", "Kaqchikel")
 * @param {number} [props.progress] - Porcentaje de progreso actual (0-100)
 * @param {string} [props.timeRemaining] - Tiempo restante para completar el curso
 * @param {string} props.badgeText - Texto del badge
 * @param {string} [props.badgeTextColor] - Color del texto del badge
 * @param {string} [props.badgeBackgroundColor] - Color de fondo del badge
 * @param {string} [props.badgeBorderColor] - Color del borde del badge
 * @param {boolean} [props.isFavorite=false] - Si el curso está marcado como favorito
 * @param {boolean} [props.isLoggedIn=false] - Si el usuario está logueado
 * @param {boolean} [props.isProcessingFavorite=false] - Si el favorito está siendo procesado
 * @param {boolean} [props.isDownloadingDiploma=false] - Si el diploma está siendo descargado
 * @param {Function} [props.onToggleFavorite] - Función ejecutada al hacer clic en favorito
 * @param {Function} [props.onClick] - Función ejecutada al hacer clic en la tarjeta
 * @param {Function} [props.onViewAgain] - Función ejecutada al hacer clic en "VER DE NUEVO"
 * @param {Function} [props.onDownloadDiploma] - Función ejecutada al hacer clic en "DIPLOMA"
 * @param {'default' | 'progress' | 'completed' | 'capacitacion-presencial'} [props.variant='default'] - Variante de la tarjeta
 * @param {string} [props.subtitle] - Subtítulo para la variante capacitacion-presencial
 * @param {string} [props.className=''] - Clases CSS adicionales
 * @param {Object} [...props] - Props adicionales del elemento div raíz
 * @returns {JSX.Element} Componente CourseCard
 */
export const CourseCard = ({
  title,
  imageUrl,
  duration,
  difficulty,
  language,
  progress,
  timeRemaining,
  badgeText,
  badgeTextColor,
  badgeBackgroundColor,
  badgeBorderColor,
  subtitle,
  isFavorite = false,
  isLoggedIn = false,
  isProcessingFavorite = false,
  isDownloadingDiploma = false,
  onToggleFavorite,
  onClick,
  onViewAgain,
  onDownloadDiploma,
  variant = "default",
  className = "",
  ...props
}) => {
  const cardClasses = ["course-card", `course-card--${variant}`, className]
    .filter(Boolean)
    .join(" ");

  const favoriteButtonClasses = [
    "course-card__favorite",
    isProcessingFavorite && "course-card__favorite--processing",
  ]
    .filter(Boolean)
    .join(" ");

  const handleCardClick = (e) => {
    if (
      e.target.closest(".course-card__favorite") ||
      e.target.closest(".course-card__actions")
    ) {
      return;
    }
    onClick?.(e);
  };

  const handleFavoriteClick = (e) => {
    e.stopPropagation();

    if (isProcessingFavorite) return;

    onToggleFavorite?.(e);
  };

  return (
    <div className={cardClasses} onClick={handleCardClick} {...props}>
      <div className="course-card__image">
        <img
          src={imageUrl}
          alt={title}
          className="course-card__img"
          onLoad={(e) => {
            e.target.classList.add("loaded");
          }}
          onError={(e) => {
            e.target.src = notFound;
            e.target.classList.add("loaded");
          }}
        />
        <div className="course-card__image-overlay"></div>
        {isLoggedIn && variant !== "capacitacion-presencial" && (
          <button
            className={favoriteButtonClasses}
            onClick={handleFavoriteClick}
            disabled={isProcessingFavorite}
          >
            {isFavorite ? <HeartFilledIcon /> : <HeartOutlineIcon />}
          </button>
        )}
      </div>

      <div className="course-card__separator"></div>

      <div className="course-card__content">
        {variant !== "capacitacion-presencial" && (
          <Badge
            textColor={badgeTextColor}
            backgroundColor={badgeBackgroundColor}
            borderColor={badgeBorderColor}
            title={badgeText}
          >
            {badgeText}
          </Badge>
        )}

        <h3 className="course-card__title">{title}</h3>

        {variant === "default" && (
          <div className="course-card__meta">
            <div className="course-card__meta-item">
              <ClockIcon />
              <span className="course-card__duration">{duration}m</span>
            </div>
            <div className="course-card__meta-item">
              <span>{difficulty}</span>
            </div>
            <div className="course-card__meta-item">
              <span>{language}</span>
            </div>
          </div>
        )}

        {variant === "progress" && (
          <div className="course-card__progress">
            <Progress
              variant="detailed"
              current={progress}
              total={100}
              timeRemaining={`${timeRemaining} min restantes`}
            />
          </div>
        )}

        {variant === "completed" && (
          <div className="course-card__actions">
            <Button
              variant="secundario-cta"
              size="small"
              onClick={onDownloadDiploma}
              disabled={isDownloadingDiploma}
            >
              {isDownloadingDiploma ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon />
              )}
              DIPLOMA
            </Button>
            <Button variant="secundario" size="small" onClick={onViewAgain}>
              VER DE NUEVO
            </Button>
          </div>
        )}

        {variant === "capacitacion-presencial" && (
          <>
            {subtitle && (
              <div className="course-card__meta">
                <span>{subtitle}</span>
              </div>
            )}
            <div className="course-card__actions">
              <Button
                variant="secundario-cta"
                size="small"
                onClick={onDownloadDiploma}
                disabled={isDownloadingDiploma}
              >
                {isDownloadingDiploma ? (
                  <CircularProgress size={16} />
                ) : (
                  <DownloadIcon />
                )}
                DIPLOMA
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
